import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faBookOpen } from '@fortawesome/free-solid-svg-icons';

const GlossaryList = ({ 
  items, 
  expandedItems, 
  onItemToggle, 
  searchTerm,
  theme 
}) => {
  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '1.5rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
    },
    header: {
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    icon: {
      fontSize: '1.25rem',
      color: theme?.primary?.blue?.[500],
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    count: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      marginLeft: 'auto',
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
    },
    item: {
      borderBottom: `1px solid ${theme?.borders?.primary}`,
      paddingBottom: '1rem',
      marginBottom: '1rem',
    },
    itemHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      padding: '0.5rem',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
    },
    itemHeaderHover: {
      background: theme?.backgrounds?.secondary,
    },
    term: {
      fontSize: '1rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      flex: 1,
    },
    arrow: {
      fontSize: '0.875rem',
      color: theme?.text?.tertiary,
      transition: 'transform 0.3s ease',
    },
    description: {
      padding: '1rem',
      paddingTop: '0.5rem',
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: theme?.text?.secondary,
    },
    highlight: {
      background: theme?.primary?.yellow?.[200],
      color: theme?.text?.primary,
      padding: '0 0.125rem',
      borderRadius: '0.125rem',
    },
  };

  // Highlight search term in text
  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} style={styles.highlight}>{part}</span>
      ) : (
        part
      )
    );
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div style={styles.header}>
        <FontAwesomeIcon icon={faBookOpen} style={styles.icon} />
        <h2 style={styles.title}>Terms & Definitions</h2>
        <span style={styles.count}>{items.length} results</span>
      </div>
      
      <div style={styles.list}>
        {items.map((item, index) => {
          const isExpanded = expandedItems.includes(item.id || index);
          
          return (
            <motion.div
              key={item.id || index}
              style={styles.item}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
            >
              <motion.div
                style={styles.itemHeader}
                whileHover={styles.itemHeaderHover}
                onClick={() => onItemToggle(item.id || index)}
              >
                <span style={styles.term}>
                  {highlightText(item.value, searchTerm)}
                </span>
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  style={{
                    ...styles.arrow,
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              </motion.div>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={styles.description}
                  >
                    {highlightText(item.description, searchTerm)}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default GlossaryList;